import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { ReactNode, RefObject, useCallback, useLayoutEffect, useRef, useState } from 'react';

// One navbar button's height. The visible list is always a whole number of these, and each chevron click moves by one.
const ITEM_HEIGHT = 64;
// A small icon with no padding.
const CHEVRON_HEIGHT = 20;

interface ScrollState {
  // Height of the visible list while it overflows; null when every app fits.
  viewportHeight: number | null;
  atTop: boolean;
  atBottom: boolean;
}

const INITIAL_STATE: ScrollState = { viewportHeight: null, atTop: true, atBottom: true };

interface NavbarScrollSectionProps {
  children: ReactNode;
  // The flexible gap below this section. Space it holds is space this section could use, so the two are measured
  // together. That sum stays the same however the space is split between them, so sizing the list can't feed back
  // into the measurement.
  spacerRef: RefObject<HTMLElement | null>;
}

// The apps list in the left nav. It takes its natural height when the window is tall enough. When it isn't, the list
// shows as many whole buttons as fit (never a half-cut one), scrolls with a hidden scrollbar, and gets up/down
// chevrons. The few pixels left over go to the spacer.
const NavbarScrollSection = ({ children, spacerRef }: NavbarScrollSectionProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<ScrollState>(INITIAL_STATE);

  const update = useCallback(() => {
    const wrapper = wrapperRef.current;
    const scroller = scrollRef.current;
    const content = contentRef.current;
    if (!wrapper || !scroller || !content) return;

    const available = wrapper.offsetHeight + (spacerRef.current?.offsetHeight ?? 0);
    // A pixel of slack absorbs sub-pixel rounding at fractional zoom levels.
    const overflowing = content.offsetHeight - available > 1;
    const viewportHeight = overflowing ? Math.max(1, Math.floor((available - 2 * CHEVRON_HEIGHT) / ITEM_HEIGHT)) * ITEM_HEIGHT : null;

    const next: ScrollState = {
      viewportHeight,
      atTop: scroller.scrollTop <= 1,
      atBottom: scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1
    };
    setState((prev) => (prev.viewportHeight === next.viewportHeight && prev.atTop === next.atTop && prev.atBottom === next.atBottom ? prev : next));
  }, [spacerRef]);

  useLayoutEffect(() => {
    update();
    if (typeof ResizeObserver === 'undefined') return;

    // The wrapper and spacer resize with the window; the content resizes when apps are enabled or disabled.
    const observer = new ResizeObserver(update);
    for (const el of [wrapperRef.current, spacerRef.current, contentRef.current]) {
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [update, spacerRef]);

  // The scroll extent changes once the viewport height is applied, so recheck which ends are reachable.
  useLayoutEffect(update, [state.viewportHeight, update]);

  const scrollBy = useCallback((top: number) => {
    scrollRef.current?.scrollBy({ top, behavior: 'smooth' });
  }, []);

  const overflowing = state.viewportHeight !== null;
  const chevronSx = { p: 0, width: '100%', height: CHEVRON_HEIGHT, borderRadius: 0 };

  return (
    <Box
      ref={wrapperRef}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        flex: '0 1 auto',
        minHeight: 0,
        overflow: 'hidden'
      }}
    >
      {overflowing && (
        <IconButton size="small" sx={chevronSx} aria-label="Scroll apps up" disabled={state.atTop} onClick={() => scrollBy(-ITEM_HEIGHT)}>
          <KeyboardArrowUp fontSize="small" />
        </IconButton>
      )}

      <Box
        ref={scrollRef}
        onScroll={update}
        data-testid="navbar-scroll-section"
        sx={{
          width: '100%',
          height: state.viewportHeight ?? 'auto',
          flex: '0 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          // Wheel and trackpad scrolling stop on button edges too, so no button is left half shown.
          scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        <Box ref={contentRef} sx={{ display: 'flex', flexDirection: 'column', width: '100%', '& > *': { scrollSnapAlign: 'start' } }}>
          {children}
        </Box>
      </Box>

      {overflowing && (
        <IconButton size="small" sx={chevronSx} aria-label="Scroll apps down" disabled={state.atBottom} onClick={() => scrollBy(ITEM_HEIGHT)}>
          <KeyboardArrowDown fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

export default NavbarScrollSection;
